'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import {
  REGION_CONFIGS,
  PRICING_REGIONS,
  getLocalizedPlanPrices,
  getRegionConfig,
  parseRegion,
  type PricingRegion,
} from '@/lib/billing/region'
import { PLANS, type FeatureGroup } from '@/lib/billing/plans'

const STORAGE_KEY = 'bb_pricing_region'
const MARKETING_STORAGE_KEY = 'bb_marketing_region'
type BillingInterval = 'monthly' | 'annual'

function normalizeCouponCode(value: string | null | undefined) {
  const trimmed = value?.trim() ?? ''
  return trimmed.length > 0 ? trimmed : null
}

function initialRegion(): PricingRegion {
  if (typeof window === 'undefined') return 'ZA'
  try {
    const stored = parseRegion(localStorage.getItem(STORAGE_KEY)) ?? parseRegion(localStorage.getItem(MARKETING_STORAGE_KEY))
    if (stored) return stored
  } catch {
    // ignore
  }
  const localeCountry = navigator.language.split('-')[1]?.toUpperCase()
  return parseRegion(localeCountry) ?? 'ZA'
}

function RegionSelectorBar({
  region,
  onSelect,
}: {
  region: PricingRegion
  onSelect: (r: PricingRegion) => void
}) {
  const cfg = getRegionConfig(region)
  return (
    <div className="rounded-2xl border border-orange-200 bg-orange-50/70 p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#c55918]">Country / currency</p>
          <p className="mt-1 text-sm text-slate-700">
            Prices shown in {cfg.currencyCode} for {cfg.label}. Change country to see local pricing.
          </p>
        </div>
        <label className="sr-only" htmlFor="pricing-region-select">Country / currency</label>
        <select
          id="pricing-region-select"
          value={region}
          onChange={(event) => onSelect(event.target.value as PricingRegion)}
          className="min-h-12 w-full rounded-xl border border-orange-200 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c55918]/20 sm:w-64"
        >
          {PRICING_REGIONS.map(r => (
            <option key={r} value={r}>
              {REGION_CONFIGS[r].label} - {REGION_CONFIGS[r].currencyCode}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {PRICING_REGIONS.map(r => {
          const regionCfg = REGION_CONFIGS[r]
          const selected = r === region
          return (
            <button
              key={r}
              type="button"
              onClick={() => onSelect(r)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                selected
                  ? 'border-[#c55918] bg-white text-[#c55918] shadow-sm'
                  : 'border-orange-200 bg-white/70 text-slate-600 hover:border-[#c55918]/50'
              }`}
            >
              <span aria-hidden>{regionCfg.flag}</span> {regionCfg.currencyCode}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── Feature group block ──────────────────────────────────────────────────────

function FeatureGroupBlock({ group }: { group: FeatureGroup }) {
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{group.title}</p>
      <ul className="space-y-1.5">
        {group.items.map(item => (
          <li key={item} className="flex items-start gap-2 text-xs text-slate-600">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c55918]" aria-hidden />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Pricing cards ────────────────────────────────────────────────────────────

function PricingCards({
  region,
  onSelectRegion,
  couponCode,
}: {
  region: PricingRegion
  onSelectRegion: (r: PricingRegion) => void
  couponCode: string | null
}) {
  const [interval, setInterval] = useState<BillingInterval>('monthly')
  const cfg = getRegionConfig(region)
  const monthlyPrices = getLocalizedPlanPrices(region)
  const freeSignupHref = `/signup?region=${region}`

  function displayPrice(planId: keyof typeof monthlyPrices, discountPct?: number) {
    const monthly = monthlyPrices[planId]
    if (monthly === 0) return { primary: `${cfg.currencySymbol}0`, sub: 'free forever', savings: false }
    if (interval === 'annual' && discountPct) {
      const annual = Math.round(monthly * 12 * (1 - discountPct / 100))
      const perMonth = Math.round(annual / 12)
      return {
        primary: `${cfg.currencySymbol}${perMonth.toLocaleString(cfg.locale)}/mo`,
        sub: `${cfg.currencySymbol}${annual.toLocaleString(cfg.locale)}/year · Save ${discountPct}%`,
        savings: true,
      }
    }
    return { primary: `${cfg.currencySymbol}${monthly}`, sub: 'per month', savings: false }
  }

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Start free now, or choose a paid plan when you are ready.</p>
            <p className="mt-1 text-sm text-slate-600">
              No card is required for the free account. Paid plans continue to checkout only after signup.
            </p>
          </div>
          <Link
            href={freeSignupHref}
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800 lg:w-auto"
          >
            <CheckCircle2 size={16} strokeWidth={2} aria-hidden />
            Create free account
          </Link>
        </div>
        {couponCode && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-[#a84915]">
            <span className="rounded bg-white px-2 py-0.5 font-mono text-[11px] font-bold tracking-[0.12em] text-[#c2571a]">
              {couponCode}
            </span>
            Offer code ready for checkout
          </div>
        )}
      </div>

      <RegionSelectorBar region={region} onSelect={onSelectRegion} />

      {/* Region summary + interval toggle row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span aria-hidden>{cfg.flag}</span>
          <span>{cfg.currencyCode} · {cfg.taxLabel} where applicable</span>
        </div>

        {/* Monthly / yearly toggle */}
        <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          <button
            onClick={() => setInterval('monthly')}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
              interval === 'monthly' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval('annual')}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
              interval === 'annual' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Yearly
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
              interval === 'annual' ? 'bg-amber-400 text-slate-900' : 'bg-amber-100 text-amber-700'
            }`}>
              Save up to 30%
            </span>
          </button>
        </div>
      </div>

      {/* Plan cards — Free → Solo → Contractor → Crew */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PLANS.map(plan => {
          const planPriceId = plan.id as keyof typeof monthlyPrices
          const price = displayPrice(planPriceId, plan.annualDiscountPct)
          const checkoutParams = new URLSearchParams()
          checkoutParams.set('region', region)
          if (plan.id !== 'free') {
            checkoutParams.set('plan', plan.id)
            checkoutParams.set('interval', interval)
            if (couponCode) checkoutParams.set('coupon', couponCode)
          }
          const checkoutQuery = checkoutParams.toString()
          const checkoutHref = `/signup?${checkoutQuery}`

          return (
            <article
              key={plan.id}
              className={`relative flex flex-col rounded-3xl border p-6 ${
                plan.mostPopular
                  ? 'border-[#c55918] bg-[#fff7f2] shadow-lg ring-1 ring-[#c55918]/15'
                  : 'border-slate-200 bg-white shadow-sm'
              }`}
            >
              {plan.mostPopular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="inline-flex items-center rounded-full bg-[#c55918] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Plan name + tagline */}
              <div className="space-y-0.5">
                <p className="text-base font-bold text-slate-900">{plan.name}</p>
                <p className="text-xs text-slate-500">{plan.tagline}</p>
              </div>

              {/* Pricing */}
              <div className="mt-4 min-h-[3.5rem]">
                <p className="text-3xl font-bold tracking-tight text-slate-900">{price.primary}</p>
                <p className={`mt-0.5 text-xs ${price.savings ? 'font-semibold text-emerald-600' : 'text-slate-400'}`}>
                  {price.sub}
                </p>
              </div>

              {/* Feature groups */}
              <div className="mt-5 flex-1 space-y-4 border-t border-slate-100 pt-4">
                {plan.featureGroups.map(group => (
                  <FeatureGroupBlock key={group.title} group={group} />
                ))}
              </div>

              {/* CTA */}
              <Link
                href={checkoutHref}
                className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                  plan.mostPopular
                    ? 'bg-[#c55918] text-white hover:bg-[#ad4f17] active:bg-[#963f12]'
                    : plan.id === 'free'
                      ? 'bg-slate-900 text-white hover:bg-slate-800'
                      : 'border border-[#c55918]/40 bg-white text-[#c55918] hover:bg-[#fff7f2]'
                }`}
              >
                {plan.id === 'free' ? (
                  <>
                    Create free account
                    <ArrowRight size={14} strokeWidth={2} aria-hidden />
                  </>
                ) : (
                  `Choose ${plan.name}`
                )}
              </Link>
            </article>
          )
        })}
      </div>

      <p className="text-center text-xs text-slate-500">
        All prices exclude {cfg.taxLabel} where applicable.
        Annual billing charged as a single upfront payment.{' '}
        <a href="mailto:info@builderbuddi.com" className="font-semibold text-[#c55918] hover:underline">
          Contact us
        </a>{' '}
        for custom or enterprise needs.
      </p>
    </div>
  )
}

// ─── Gate ─────────────────────────────────────────────────────────────────────

export function PricingRegionGate({
  couponCode: initialCouponCode = null,
  initialRegion: initialRegionParam = null,
}: {
  couponCode?: string | null
  initialRegion?: PricingRegion | null
}) {
  const couponCode = normalizeCouponCode(initialCouponCode)
  const [region, setRegion] = useState<PricingRegion>(() => initialRegionParam ?? initialRegion())

  function selectRegion(r: PricingRegion) {
    try {
      localStorage.setItem(STORAGE_KEY, r)
      localStorage.setItem(MARKETING_STORAGE_KEY, r)
    } catch { /* ignore */ }
    setRegion(r)
  }

  return <PricingCards region={region} onSelectRegion={selectRegion} couponCode={couponCode} />
}
